import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Center,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { FormattedMessage } from "react-intl";
import IpCheckerTable from "../IpCheckerTable";
import { useIpQueryContext } from "context/IpQueryContext";
import "./IpCheckerTabs.css";
import useApiRequest from "hooks/api/useApiHook";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import ErrorText from "components/API/ErrorText";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import { getCountryCodeForFlag } from "utils/countryMapping";
import { GetSubscription } from "api/GetSubscription";
import { GetRequestsLeft } from "api/GetRequestsLeft";
import NoSubscriptionComponent from "../NoSubscriptionComponent";
import PortCheckBlock from "../PortCheckBlock";
import { usePortCheckAvailability } from "hooks/api/use-port-check-availability";
import CheckIpErrorMessage from "../CheckIpErrorMessage";
import { checkIpQueryMode } from "utils/apiChecker/settings";

const IpCheckerTabs = ({ modeEnabled = true }: { modeEnabled?: boolean }) => {
  const [tabsList, setTabslist] = useState<any[]>([]);
  const [country, setCountry] = useState(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [hasRequests, setHasRequests] = useState<boolean | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const { queryIp } = useIpQueryContext();
  const defaultApiKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const userIp = useSelector((state: RootState) => state.userIp.userIp);
  const appDefaultApiKey = process.env.REACT_APP_DEFAULT_API_KEY;

  // `modeEnabled` is the checker page switch: checked = custom check, sent as
  // `?mode=false` (mapping pinned in utils/apiChecker/settings.test.ts).
  const checkIpParams = useMemo(
    () => ({ queryIp, mode: checkIpQueryMode(modeEnabled) }),
    [queryIp, modeEnabled]
  );

  // console.log("🚀 [PAID_VERSION] RENDERING PAID VERSION IpCheckerTabs with queryIp:", queryIp);
  // console.log("🚀 [PAID_VERSION] modeEnabled:", modeEnabled, "mode:", !modeEnabled);
  // console.log("🚀 [PAID_VERSION] checkIpParams:", checkIpParams);

  // Use useApiRequest hook only if queryIp exists
  const { data, loading, error } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.CHECK_IP,
    apiToken: defaultApiKey,
    checkIpParams,
  }, queryIp ? null : null); // Don't make request if there's no queryIp

  // Check subscription and requests on component mount
  useEffect(() => {
    const checkAccess = async () => {
      if (defaultApiKey && defaultApiKey !== appDefaultApiKey) {
        setCheckingAccess(true);
        try {
          // Check subscription
          try {
            const subscriptionData = await GetSubscription(defaultApiKey);
            if (subscriptionData?.data?.data) {
              const subData = subscriptionData.data.data;
              // Check that object is not empty and contains product_name
              const hasValidSubscription = subData && 
                typeof subData === 'object' &&
                Object.keys(subData).length > 0 &&
                subData.product_name && 
                subData.product_name !== null &&
                subData.product_name !== undefined &&
                subData.product_name !== "";
              setHasSubscription(hasValidSubscription);
            } else {
              setHasSubscription(false);
            }
          } catch (error) {
            // console.error("Error checking subscription:", error);
            setHasSubscription(false);
          }

          // Check requests
          try {
            const requestsData = await GetRequestsLeft(defaultApiKey);
            if (requestsData?.data) {
              // API returns requests_left, not requests
              setHasRequests((requestsData.data.requests_left || requestsData.data.requests || 0) > 0);
            } else {
              setHasRequests(false);
            }
          } catch (error) {
            // console.error("Error checking requests:", error);
            setHasRequests(false);
          }
        } catch (error) {
          // console.error("Error checking access:", error);
          setHasSubscription(false);
          setHasRequests(false);
        } finally {
          setCheckingAccess(false);
        }
      } else {
        setHasSubscription(false);
        setHasRequests(false);
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [defaultApiKey, appDefaultApiKey]);

  // IP whose paid port-check result is on screen: keeps the block (and its
  // result) mounted after the check that spent the last request.
  const [portCheckShownIp, setPortCheckShownIp] = useState<string | null>(null);
  // Hidden until the api confirms the feature is live (fails closed).
  const portCheckAvailable = usePortCheckAvailability();

  // A charged port check spends a request: re-read the quota the same way
  // checkAccess does, so `hasRequests` and the block's cost label follow.
  const refreshRequestsLeft = useCallback(async () => {
    setPortCheckShownIp(queryIp);
    if (!defaultApiKey || defaultApiKey === appDefaultApiKey) return;
    try {
      const requestsData = await GetRequestsLeft(defaultApiKey);
      setHasRequests(
        (requestsData?.data?.requests_left || requestsData?.data?.requests || 0) > 0
      );
    } catch (error) {
      // Keep the last known value; the server still refuses an unpaid call.
    }
  }, [queryIp, defaultApiKey, appDefaultApiKey]);

  // console.log("🔍 [IP_CHECKER] API Response data:", data);
  // console.log("🔍 [IP_CHECKER] API Response error:", error);
  // console.log("🔍 [IP_CHECKER] API Response loading:", loading);

  useEffect(() => {
    // A failed lookup (429 rate limit, 503 provider outage, network) must not
    // leave the previous IP's result on screen: `data` survives a failed
    // re-fetch in useApiRequest.
    if (error) {
      setTabslist([]);
      setCountry(null);
      return;
    }
    // Don't process data until access check is complete
    if (!data || checkingAccess || hasSubscription === null || hasRequests === null) {
      return;
    }
    
      // console.log("🔍 [IP_CHECKER] Received data:", data);
      // console.log("🔍 [IP_CHECKER] data.data structure:", data.data);
    
    // Check if there are nested providers (full data)
    // IMPORTANT: convert to boolean value!
    const hasNestedProviders = Boolean(
      data.data?.IpApi || data.data?.IpInfo || data.data?.IpData || data.data?.Whois || 
      data.data?.AbuseIpDb || data.data?.IpQualityScore || data.data?.Scamalytics
    );
    
    // If API returned full data but user has no access (no requests), don't process data
    if (hasNestedProviders && !hasRequests) {
      setTabslist([]);
      setCountry(null);
      return;
    }
      
      // Set country from API response
      const countryToSet = data.data?.IpApi?.country || 
                           data.data?.IpInfo?.country || 
                           data.data?.IpData?.country_code || 
                           data.data?.country || 
                           data.country;
      setCountry(countryToSet);

      // Build list of tabs for API
    // Important: full data (hasNestedProviders) is available only with subscription
      if (data.data) {
        const dataList = [];
        
        if (hasNestedProviders) {
          // Full mode: nested structure with separate providers
          // Available when there are requests (with or without subscription)
          if (!hasRequests) {
            // If there are no requests, don't create tabs
            setTabslist([]);
            return;
          }
          
          // console.log("🔍 [IP_CHECKER] Full mode: nested structure");
          
          // Add each service as a separate tab
          if (data.data.IpApi) {
            dataList.push({ title: "IpAPI Details", data: [{ ...data.data.IpApi }] });
          }
          if (data.data.IpInfo) {
            dataList.push({ title: "IpInfo Details", data: [{ ...data.data.IpInfo }] });
          }
          if (data.data.IpData) {
            dataList.push({ title: "IpData Details", data: [{ ...data.data.IpData }] });
          }
          if (data.data.Whois) {
            dataList.push({ title: "Whois Details", data: [{ ...data.data.Whois }] });
          }
          
          // If there are other services, add them
          if (data.data.IpQualityScore) {
            dataList.push({ title: "IpQualityScore", data: [{ ...data.data.IpQualityScore }] });
          }
          if (data.data.AbuseIpDb) {
            dataList.push({ title: "AbuseIPDB", data: [{ ...data.data.AbuseIpDb }] });
          }
          if (data.data.Scamalytics) {
            dataList.push({ title: "Scamalytics", data: [{ ...data.data.Scamalytics }] });
          }
          if (data.data.IpIntel) {
            dataList.push({ title: "IpIntel", data: [{ ...data.data.IpIntel }] });
          }
          
          // Add general tab with basic information
          dataList.push({ 
            title: "General Info", 
            data: [{ 
              ip: data.data.IpApi?.query || data.data.IpInfo?.ip,
              country: data.data.IpApi?.country || data.data.IpInfo?.country,
              city: data.data.IpApi?.city || data.data.IpInfo?.city,
              region: data.data.IpApi?.regionName || data.data.IpInfo?.region,
              isp: data.data.IpApi?.isp || data.data.IpInfo?.org,
              asn: data.data.IpApi?.as || data.data.IpInfo?.org,
              timezone: data.data.IpApi?.timezone || data.data.IpInfo?.timezone
            }] 
          });
        } else {
          // Basic mode: flat data structure
          // console.log("🔍 [IP_CHECKER] Basic mode: flat structure");
          
          // Create tab with general data from flat structure
          dataList.push({ 
            title: "General Info", 
            data: [{ ...data.data }] 
          });
        }
        
        // console.log("🔍 [IP_CHECKER] Generated tab list:", dataList);
        
        setTabslist(dataList);
      } else if (data?.IpInfo) {
        // Old API structure (fallback)
        // Check requests for old structure too
        const hasOldStructureProviders = data?.IpInfo || data?.IpApi || data?.IpData || data?.IpQualityScore || data?.AbuseIpDb || data?.Scamalytics;
        if (hasOldStructureProviders && !hasRequests) {
          setTabslist([]);
          return;
        }
        
        const dataList = [
          { title: "IpInfo", data: [{ ...data?.IpInfo }] },
          { title: "IpIntel", data: [{ ...data?.IpIntel }] },
          { title: "IpData", data: [{ ...data?.IpData }] },
          { title: "IpApi", data: [{ ...data?.IpApi }] },
          { title: "IpQualityScore", data: [{ ...data?.IpQualityScore }] },
          { title: "AbuseIPDB", data: [{ ...data?.AbuseIpDb }] },
          { title: "Scamalytics", data: [{ ...data?.Scamalytics }] },
        ];
        setTabslist(dataList);
      } else {
        // Fallback for other structures - only if there are no full provider data
        const dataList = [{ title: "Lookup result", data: [{ ...data }] }];
        setTabslist(dataList);
      }
  }, [data, loading, error, checkingAccess, hasSubscription, hasRequests]);

  return (
    <main className="ip-checker-tabs">
      <Center>
        <div className="ip-heading">
          {country && tabsList.length && !loading ? (
            <img
              width={50}
              height={40}
              src={`/flags/${getCountryCodeForFlag(country)}.svg`}
              alt={country}
            />
          ) : null}
          <Heading className="query-ip-address">{queryIp}</Heading>
        </div>
      </Center>
      {loading || checkingAccess || hasSubscription === null || hasRequests === null ? (
        <Center>
          <LoaderSpinner />
        </Center>
      ) : error ? (
        <CheckIpErrorMessage error={error} />
      ) : (
        <Box>
          {/* Check if API returned full data but user has no access (no requests) */}
          {data?.data && (data.data.IpApi || data.data.IpInfo || data.data.IpData || data.data.Whois || 
                          data.data.AbuseIpDb || data.data.IpQualityScore || data.data.Scamalytics) && !hasRequests ? (
            <Box>
              <Box 
                bg="status.warningSubtle"
                p={3} 
                mb={4} 
                borderRadius="md" 
                border="1px solid" 
                borderColor="status.warning"
              >
                <Text color="text.primary" fontWeight="medium">
                  <FormattedMessage id="BasicCheckMessageText" defaultMessage="You need a subscription to access this data." />
                </Text>
              </Box>
              <NoSubscriptionComponent />
            </Box>
          ) : !tabsList.length ? (
            <Center>
              <LoaderSpinner />
            </Center>
          ) : (
            <>
          {/* Show subscription information */}
          {data?.data?._subscription_required && (
            <Box 
              bg="status.warningSubtle"
              p={3} 
              mb={4} 
              borderRadius="md" 
              border="1px solid" 
              borderColor="status.warning"
            >
              <Text color="text.primary" fontWeight="medium">
                {data.data._message || <FormattedMessage id="BasicCheckMessageText" />}
              </Text>
            </Box>
          )}
          
          <Tabs>
            <TabList>
              {tabsList.map((tab, index) => (
                <Tab className="checker-block-tab-button" key={index}>
                  {tab.title}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {tabsList.map((tab, index) => (
                <TabPanel key={index}>
                  <IpCheckerTable title={tab.title} data={tab.data} type="full" />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
            </>
          )}
        </Box>
      )}
      {/* Port check under the IP result. With requests left the checked IP
          is sent (paid, label states the cost); without, only the visitor's
          own IP may be checked, so the block is hidden for any other IP. */}
      {portCheckAvailable &&
        tabsList.length > 0 &&
        !loading &&
        !checkingAccess &&
        (hasRequests || portCheckShownIp === queryIp ? (
          <PortCheckBlock
            key={queryIp}
            ip={queryIp}
            apiToken={defaultApiKey}
            isPaid={Boolean(hasRequests)}
            onCharged={refreshRequestsLeft}
          />
        ) : userIp && queryIp === userIp ? (
          // No requests left: own IP only, anonymous (no cost, no token).
          <PortCheckBlock key={queryIp} />
        ) : null)}
      {!tabsList.length && data && !error && <ErrorText />}
    </main>
  );
};

export default IpCheckerTabs;
